import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const STATUSES = [
  { v: "present", c: "bg-emerald-100 text-emerald-800" },
  { v: "absent", c: "bg-rose-100 text-rose-800" },
  { v: "late", c: "bg-amber-100 text-amber-800" },
  { v: "leave", c: "bg-sky-100 text-sky-800" },
];

export default function Attendance() {
  const { user: me } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [subjectType, setSubjectType] = useState("student");
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [records, setRecords] = useState([]);

  const load = async () => {
    const [stu, people, att] = await Promise.all([
      api.get("/students"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/attendance", { params: { date, subject_type: subjectType } }),
    ]);
    setStudents(stu.data);
    setStaff(people.data.filter((u) => ["teacher", "staff", "accounts", "principal"].includes(u.role)));
    setRecords(att.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, subjectType]);

  const map = useMemo(() => {
    const m = {};
    records.forEach((r) => { m[r.person_id] = r.status; });
    return m;
  }, [records]);

  const mark = async (person_id, status, class_name, section) => {
    try {
      await api.post("/attendance", { date, subject_type: subjectType, person_id, status, class_name, section });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rows = subjectType === "student"
    ? students.map((s) => ({ id: s.id, name: s.name, meta: `Class ${s.class_name}-${s.section} · Roll ${s.roll_no}`, class_name: s.class_name, section: s.section }))
    : staff.map((u) => ({ id: u.id, name: u.name, meta: `${u.role} · ${u.email}`, class_name: null, section: null }));

  const canMark = ["admin", "principal", "teacher"].includes(me.role);

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <h1 className="font-display text-3xl font-black">Attendance</h1>
        <p className="text-zinc-500 mt-1">Track student and staff attendance class-wise and section-wise.</p>
      </div>

      <Card className="p-4 border-zinc-200">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date" className="w-44 mt-1" />
          </div>
          {["admin", "principal"].includes(me.role) && (
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={subjectType} onValueChange={setSubjectType}>
              <SelectTrigger data-testid="attendance-type-select" className="w-44 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="student">Students</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
          )}
        </div>
      </Card>

      <Card className="border-zinc-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Details</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const current = map[r.id];
              return (
                <TableRow key={r.id} className="row-hover">
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-zinc-500 text-sm">{r.meta}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {STATUSES.map((s) => (
                        <button
                          key={s.v}
                          disabled={!canMark}
                          data-testid={`attendance-${r.id}-${s.v}`}
                          onClick={() => mark(r.id, s.v, r.class_name, r.section)}
                          className={`text-[10px] px-2 py-1 rounded uppercase tracking-wide font-semibold transition-colors ${
                            current === s.v ? s.c : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
                          }`}
                        >
                          {s.v}
                        </button>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-center py-8 text-zinc-500">Nothing to mark.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
